import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { label: "How it works", href: "#how" },
  { label: "Website Score", href: "#score" },
  { label: "Pricing", href: "#pricing" },
  { label: "Plans", href: "#plans" },
  { label: "FAQ", href: "#faq" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }), 350);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goBlog = () => { setOpen(false); navigate("/blog"); };
  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-black/70 border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <button onClick={goHome} className="font-display font-black text-lg md:text-xl tracking-tighter" data-testid="nav-logo">
          brand<span className="text-[#ccff00]">to</span>built
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button key={l.href} onClick={() => go(l.href)} className="text-sm text-zinc-300 hover:text-[#ccff00] transition-colors" data-testid={`nav-link-${l.href.slice(1)}`}>
              {l.label}
            </button>
          ))}
          <button onClick={goBlog} className="text-sm text-zinc-300 hover:text-[#ccff00] transition-colors" data-testid="nav-link-blog">
            Blog
          </button>
        </nav>

        <div className="hidden md:block">
          <button onClick={() => go("#contact")} className="rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:scale-105 transition-transform" data-testid="nav-cta">
            Start now
          </button>
        </div>

        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 border-b border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button key={l.href} onClick={() => go(l.href)} className="text-left text-lg text-zinc-200" data-testid={`nav-mobile-${l.href.slice(1)}`}>
                  {l.label}
                </button>
              ))}
              <button onClick={goBlog} className="text-left text-lg text-zinc-200" data-testid="nav-mobile-blog">Blog</button>
              <button onClick={() => go("#contact")} className="rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-sm px-6 py-3 mt-2" data-testid="nav-mobile-cta">
                Start now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Nav;
